import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * IntersectionObserver scroll reveal (see .fx-reveal / .fx-reveal-visible in globals).
 *
 * Must observe **new** nodes whenever the DOM updates (e.g. boutique pagination) —
 * otherwise cards stay at opacity:0 until some unrelated interaction triggers layout.
 */
export default function FxRevealObserver() {
  const location = useLocation();

  useEffect(() => {
    const rootEl = document.getElementById("root") ?? document.body;

    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: "120px 0px 80px 0px",
      threshold: [0, 0.01, 0.05, 0.1],
    };

    function handleIntersect(
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver
    ) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("fx-reveal-visible");
          observer.unobserve(entry.target);
        }
      });
    }

    const observer = new IntersectionObserver(handleIntersect, options);

    const observeAll = () => {
      document.querySelectorAll(".fx-reveal:not(.fx-reveal-visible)").forEach((el) => {
        observer.observe(el);
      });
    };

    let rafId = 0;
    const scheduleObserveAll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        observeAll();
      });
    };

    observeAll();
    const t1 = window.setTimeout(scheduleObserveAll, 120);
    const t2 = window.setTimeout(scheduleObserveAll, 400);

    const mutationObserver = new MutationObserver(scheduleObserveAll);
    mutationObserver.observe(rootEl, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      cancelAnimationFrame(rafId);
      mutationObserver.disconnect();
      observer.disconnect();
    };
  }, [location.pathname, location.search]);

  return null;
}
